import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Dialog, DialogContent, DialogTitle, DialogHeader, DialogDescription } from "@/components/ui/dialog";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import {
  BookOpen,
  BarChart3,
  ClipboardCheck,
  Users,
  TrendingUp,
  CheckCircle2,
  Shield,
  Clock,
  Award,
  GraduationCap,
  ArrowRight,
  Star,
  Instagram,
  Linkedin,
} from "lucide-react";
import { motion } from "framer-motion";
import { format, parseISO } from "date-fns";
import logo from "@/assets/logo.png";
import productVideo from "@/assets/tool-demo-video.mp4.asset.json";
import productVideoPoster from "@/assets/certification-platform-fallback.jpg.asset.json";
import { supabase } from "@/integrations/supabase/client";
import { ScrollReveal, FadeUpItem } from "@/components/landing/ScrollReveal";
import { AnimatedCounter } from "@/components/landing/AnimatedCounter";
import { PLAN_PRICES, yearlySavingsPct, quarterlySavingsPct, PLAN_INCLUDED_FEATURES } from "@/lib/pricing";
import { sanitizeHtml } from "@/lib/sanitize";
import { CardGridSkeleton } from "@/components/CardGridSkeleton";
import { INITIAL_CERTS_VISIBLE } from "./landingCertsVisibility";
import { ExpandableGrid } from "@/components/ExpandableGrid";
import { ScrollToTopButton } from "@/components/landing/ScrollToTopButton";
import { LandingHeader } from "@/components/landing/LandingHeader";
import { IS_BETA } from "@/lib/platformConfig";
import { SeoHead } from "@/components/SeoHead";

interface LandingCert {
  certification_id: string;
  name: string;
  full_name: string | null;
  code: string;
  version: string;
  level: string;
  status: string;
  description: string | null;
  badges?: string[] | null;
  available_until?: string | null;
}

interface CertStats {
  questions_count: number;
  mock_sets_count: number;
  keywords_count: number;
  chapters_count: number;
  explanations_count: number;
}

// Hardcoded fallback shown immediately on the Landing page so visitors NEVER
// see an empty "Certifications coming soon" state — even if the API is slow,
// blocked, or unreachable. Live data from Supabase replaces this list as soon
// as it arrives. Keep this list in sync with the certifications flagged
// `show_on_landing = true` in the database.
const FALLBACK_LANDING_CERTS: LandingCert[] = [
  {
    certification_id: "ctfl-401",
    name: "ISTQB CTFL",
    full_name: "ISTQB Certified Tester Foundation Level",
    code: "CTFL",
    version: "4.0.1",
    level: "Foundation",
    status: "active",
    description:
      "The internationally recognized entry-level certification for software testers, covering testing fundamentals, the test process, static and dynamic techniques, test management and tool support.",
    badges: ["most_popular"],
  },
  {
    certification_id: "ct-genai-v10",
    name: "ISTQB CT-GenAI",
    full_name: "ISTQB Certified Tester - AI Testing (Generative AI)",
    code: "CT-GenAI",
    version: "1.0",
    level: "Specialist",
    status: "active",
    description:
      "Specialist certification focused on testing generative AI systems, including prompt engineering, model evaluation, hallucination detection, and quality risks unique to LLM-powered software.",
  },
];

const heroContainer = {
  hidden: {},
  visible: { transition: { staggerChildren: 0.15 } },
};

const heroItem = {
  hidden: { opacity: 0, y: 24 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.6, ease: [0.25, 0.1, 0.25, 1] as const } },
};

const features = [
  {
    icon: BookOpen,
    title: "Chapter-Based Practice",
    description:
      "Practice questions organized by syllabus chapters with explanations and progress tracking.",
  },
  {
    icon: BarChart3,
    title: "Performance Analytics",
    description:
      "Track progress, chapter performance, learning objectives, and readiness insights.",
  },
  {
    icon: ClipboardCheck,
    title: "Timed Mock Exams",
    description:
      "Simulate the real certification experience with timed mock exams.",
  },
  {
    icon: GraduationCap,
    title: "Keywords & Flashcards",
    description:
      "Master certification terminology through matching, categorization, and flashcards.",
  },
  {
    icon: TrendingUp,
    title: "Readiness Insights",
    description:
      "Identify knowledge gaps and understand which topics need more attention.",
  },
  {
    icon: Award,
    title: "Structured Preparation",
    description:
      "Follow a clear learning path designed for software testing certifications.",
  },
];

const Landing = () => {
  // Seed with the hardcoded fallback so the section is populated on first
  // paint. Live API data overrides this once available.
  const [landingCerts, setLandingCerts] = useState<LandingCert[]>(FALLBACK_LANDING_CERTS);
  const [certsLoaded, setCertsLoaded] = useState(true);
  const [menuOpen, setMenuOpen] = useState(false);
  const [dialogCert, setDialogCert] = useState<LandingCert | null>(null);
  const [certDialogOpen, setCertDialogOpen] = useState(false);
  // Keep the last cert rendered while the dialog closes, so the exit
  // animation fades real content instead of an empty box (no flash).
  const selectedCert = dialogCert;
  const setSelectedCert = (cert: LandingCert | null) => {
    if (cert) {
      setDialogCert(cert);
      setCertDialogOpen(true);
    } else {
      setCertDialogOpen(false);
    }
  };
  const [certStats, setCertStats] = useState<Record<string, CertStats>>({});
  const [descExpanded, setDescExpanded] = useState(false);

  useEffect(() => {
    setDescExpanded(false);
  }, [selectedCert?.certification_id]);

  useEffect(() => {
    let cancelled = false;
    let attempt = 0;

    const fetchCerts = async () => {
      attempt += 1;
      const { data, error } = await supabase
        .from("certifications")
        .select("certification_id, name, full_name, code, version, level, status, description, badges, available_until")
        .eq("show_on_landing", true)
        .order("sort_order");

      if (cancelled) return;

      if (error) {
        // Keep the hardcoded fallback visible and retry quietly in the
        // background with exponential backoff capped at 15s. Visitors never
        // see an empty / "coming soon" state regardless of API health.
        const delay = Math.min(1000 * 2 ** Math.min(attempt - 1, 4), 15000);
        setTimeout(() => {
          if (!cancelled) void fetchCerts();
        }, delay);
        return;
      }

      // Only replace the fallback when the API returns at least one row —
      // an empty live response (e.g. RLS misconfiguration) must NOT wipe out
      // the hardcoded preview.
      const rows = (data ?? []) as LandingCert[];
      if (rows.length > 0) {
        setLandingCerts(rows);
      }
      setCertsLoaded(true);
    };

    void fetchCerts();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const { data, error } = await (supabase as any).rpc("get_landing_cert_stats");
      if (cancelled || error || !Array.isArray(data)) return;
      const map: Record<string, CertStats> = {};
      for (const row of data) {
        if (row && typeof row.certification_id === "string") {
          map[row.certification_id] = {
            questions_count: row.questions_count ?? 0,
            mock_sets_count: row.mock_sets_count ?? 0,
            keywords_count: row.keywords_count ?? 0,
            chapters_count: row.chapters_count ?? 0,
            explanations_count: row.explanations_count ?? 0,
          };
        }
      }
      setCertStats(map);
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const scrollTo = (id: string) => {
    document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <div className="min-h-screen bg-background scroll-smooth">
      <SeoHead
        title="Certification Prep | A Software Tester"
        description="Identify knowledge gaps before your certification exam. Syllabus-aligned practice, full mock exams, and readiness insights for ISTQB candidates."
        path="/"
        jsonLd={{
          "@context": "https://schema.org",
          "@type": "FAQPage",
          mainEntity: [
            { "@type": "Question", name: "Which certifications are supported?", acceptedAnswer: { "@type": "Answer", text: "We currently focus on ISTQB-aligned certifications, with more being added over time. Each certification has its own dedicated question bank, mock exams, and chapters." } },
            { "@type": "Question", name: "How are the practice questions and mock exams structured?", acceptedAnswer: { "@type": "Answer", text: "Practice questions are organised by chapter and learning objective so you can target weak areas. Mock exams replicate the official exam format (same question count, time limit, and passing threshold)." } },
            { "@type": "Question", name: "Does this platform provide official certification?", acceptedAnswer: { "@type": "Answer", text: "No. We are an independent exam-preparation platform and are not affiliated with ISTQB or any other certification body." } },
            { "@type": "Question", name: "How does the subscription work?", acceptedAnswer: { "@type": "Answer", text: "Choose Monthly, Quarterly, or Yearly. Each subscription is purchased for a single certification and unlocks its full question bank, mock exams, keyword practice, and detailed insights. Billing renews automatically at the end of each period until you cancel." } },
            { "@type": "Question", name: "Can I cancel anytime?", acceptedAnswer: { "@type": "Answer", text: "Yes. You can cancel your subscription at any time from your account settings. You'll keep access until the end of your current billing period." } },
            { "@type": "Question", name: "Is my data secure?", acceptedAnswer: { "@type": "Answer", text: "Yes. Authentication, role-based access, and per-user data isolation are enforced at the database level." } },
          ],
        }}
      />
      {/* Navigation — DO NOT inline this markup. Header & mobile menu are
          extracted so future text edits to this file cannot accidentally
          remove FAQ, hover styles, or the scroll-to-top logo. To add or
          remove a nav link, edit `src/components/landing/navConfig.ts`. */}
      <LandingHeader
        menuOpen={menuOpen}
        onToggleMenu={() => setMenuOpen(!menuOpen)}
        onCloseMenu={() => setMenuOpen(false)}
      />

      {/* Hero */}
      <section className="relative overflow-hidden pt-8 pb-8 md:pt-12 md:pb-12 px-6 bg-background">
        <div className="absolute inset-0 hero-blueprint pointer-events-none" aria-hidden="true" />
        <div className="container relative mx-auto max-w-4xl text-center">
          <motion.div variants={heroContainer} initial="hidden" animate="visible">
            <motion.p
              variants={heroItem}
              className="text-sm font-medium text-primary mb-4 md:mb-5 tracking-wide uppercase"
            >
              Certification Preparation Platform
            </motion.p>
            <motion.h1
              variants={heroItem}
              className="text-3xl sm:text-[2.75rem] md:text-[3.4rem] font-bold tracking-tight text-foreground mb-10 md:mb-12 leading-[1.2] md:leading-[1.25]"
            >
              <span className="block">
                Identify{" "}
                <span className="relative inline-block">
                  <span className="relative z-10">Knowledge Gaps</span>
                  <motion.svg
                    className="absolute -left-[2%] -right-[2%] bottom-[0.05em] w-[104%] h-[0.7em] pointer-events-none z-0"
                    viewBox="0 0 300 30"
                    fill="none"
                    preserveAspectRatio="none"
                    aria-hidden="true"
                  >
                    <motion.path
                      d="M6 16 C60 6, 150 24, 294 12"
                      stroke="#f9a8d4"
                      strokeOpacity="0.4"
                      strokeWidth="22"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      initial={{ pathLength: 0, opacity: 0 }}
                      animate={{ pathLength: 1, opacity: 1 }}
                      transition={{ duration: 1.0, delay: 0.6, ease: "easeOut" }}
                    />
                  </motion.svg>
                </span>
              </span>
              <span className="block">
                Before Your{" "}
                <span className="relative inline-block">
                  <span className="relative z-10">Certification</span>
                  <motion.svg
                    className="absolute left-0 right-0 bottom-[0.05em] w-full h-[0.7em] pointer-events-none z-0"
                    viewBox="0 0 300 30"
                    fill="none"
                    preserveAspectRatio="none"
                    aria-hidden="true"
                  >
                    <motion.path
                      d="M6 16 C60 6, 150 24, 294 12"
                      stroke="hsl(var(--primary))"
                      strokeOpacity="0.28"
                      strokeWidth="22"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      initial={{ pathLength: 0, opacity: 0 }}
                      animate={{ pathLength: 1, opacity: 1 }}
                      transition={{ duration: 1.0, delay: 1.7, ease: "easeOut" }}
                    />
                  </motion.svg>
                </span>{" "}
                Exam
              </span>
            </motion.h1>
            <motion.p
              variants={heroItem}
              className="text-base md:text-xl text-muted-foreground mb-10 md:mb-12 max-w-2xl mx-auto leading-relaxed"
            >
              Practice realistic software testing certification questions, track progress, and identify areas for improvement before taking the real exam.
            </motion.p>
            <motion.div
              variants={heroItem}
              className="flex flex-col sm:flex-row items-stretch sm:items-center justify-center gap-3 sm:gap-4 max-w-md sm:max-w-none mx-auto"
            >
              <Button size="lg" className="px-8 gap-2 w-full sm:w-auto" asChild>
                <Link to="/signup">
                  Start Preparing <ArrowRight className="h-4 w-4" />
                </Link>
              </Button>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* Product Video — sits directly under the hero so the product is
          visible without scrolling on common desktop resolutions. */}
      <ProductVideoSection />

      {/* Statistics */}
      <section className="py-8 md:py-12 px-6 bg-muted/70">
        <div className="container mx-auto max-w-6xl">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 md:gap-12">
            <AnimatedCounter end={500} suffix="+" label="Practice Questions" />
            <AnimatedCounter end={6} label="Certifications Available" />
            <AnimatedCounter end={10} suffix="+" label="Mock Exam Sets" />
          </div>
        </div>
      </section>

      {/* Certifications Overview */}
      <section id="certifications" className="pt-14 pb-12 md:pt-24 md:pb-24 px-6 bg-background">
        <div className="container mx-auto max-w-6xl">
          <ScrollReveal>
            <h2 className="text-2xl md:text-3xl font-semibold text-foreground text-center mb-3 md:mb-4">
              Certifications We Cover
            </h2>
            <p className="text-muted-foreground text-center mb-8 md:mb-16 max-w-xl mx-auto">
              Start with ISTQB Foundation and grow your credentials. More certifications are on the way.
            </p>
          </ScrollReveal>

          <ScrollReveal variant="stagger">
            <div className="relative min-h-[276px]">
              {!certsLoaded ? (
                <CardGridSkeleton label="Loading certifications" />
              ) : landingCerts.length === 0 ? (
                <p className="text-muted-foreground col-span-full text-center py-8">
                  Certifications coming soon - stay tuned!
                </p>
              ) : (
                <ExpandableGrid
                  items={landingCerts}
                  initialVisible={INITIAL_CERTS_VISIBLE}
                  gridClassName="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6"
                  getItemKey={(cert) => cert.certification_id}
                  showMoreLabel={(n) => `Show more (${n})`}
                  renderItem={(cert) => {
                    const isActive = cert.status === "active";
                    const isMostPopular = Array.isArray(cert.badges) && cert.badges.includes("most_popular");
                    const availableUntilLabel = cert.available_until
                      ? format(parseISO(cert.available_until), "d MMM yyyy")
                      : null;
                    return (
                      <Card
                        className={`relative overflow-hidden h-full cursor-pointer transition-[box-shadow,transform] duration-300 ease-out hover:-translate-y-1 hover:shadow-lg ${isActive ? "border-2 border-primary shadow-md" : "border-2 border-dashed border-muted-foreground/40 shadow-sm opacity-75"}`}
                        onClick={() => setSelectedCert(cert)}
                      >
                        <div className="absolute top-3 right-3">
                          {!isActive ? (
                            <span className="inline-flex items-center gap-1 rounded-full bg-muted px-2.5 py-0.5 text-xs font-medium text-muted-foreground">
                              <Clock className="h-3 w-3" /> Coming Soon
                            </span>
                          ) : availableUntilLabel ? (
                            <span className="inline-flex items-center gap-1 whitespace-nowrap rounded-full bg-destructive text-destructive-foreground px-2.5 py-0.5 text-xs font-medium">
                              <Clock className="h-3 w-3 shrink-0" /> Until {availableUntilLabel}
                            </span>
                          ) : isMostPopular ? (
                            <span className="inline-flex items-center gap-1 whitespace-nowrap rounded-full bg-primary text-primary-foreground px-2.5 py-0.5 text-xs font-medium">
                              <Star className="h-3 w-3 shrink-0" /> Most popular
                            </span>
                          ) : null}
                        </div>
                        <CardContent className="pt-8 pb-6 px-6 flex flex-col min-h-[220px]">
                          <div
                            className={`w-12 h-12 rounded-lg flex items-center justify-center mb-5 ${isActive ? "bg-primary/10" : "bg-muted"}`}
                          >
                            {isActive ? (
                              <Award className="h-6 w-6 text-primary" />
                            ) : (
                              <GraduationCap className="h-6 w-6 text-muted-foreground" />
                            )}
                          </div>
                          {(() => {
                            const rawFullName = cert.full_name || cert.name;
                            const displayFullName = rawFullName.replace(/\s+v?\d+(\.\d+)*\s*$/i, "").trim();
                            return (
                              <h3 className="text-lg font-bold text-foreground mb-3">{displayFullName}</h3>
                            );
                          })()}
                          {cert.description && (
                            <p className="text-sm text-muted-foreground leading-relaxed line-clamp-3 flex-1 text-justify">
                              {cert.description.replace(/<[^>]*>/g, "")}
                            </p>
                          )}
                           <p className="text-xs text-primary font-medium mt-3 flex items-center gap-1">
                             View details <ArrowRight className="h-3 w-3" aria-hidden="true" />
                           </p>
                        </CardContent>
                      </Card>
                    );
                  }}
                  trailingSlot={
                    landingCerts.length > 0 && landingCerts.length < 3 ? (
                      <FadeUpItem>
                        <Card className="border-dashed border-2 border-border shadow-none h-full">
                          <CardContent className="pt-8 pb-6 px-6 flex flex-col min-h-[220px] h-full">
                            <div className="w-12 h-12 rounded-lg bg-muted flex items-center justify-center mb-5">
                              <GraduationCap className="h-6 w-6 text-muted-foreground" />
                            </div>
                            <h3 className="text-lg font-semibold text-foreground mb-1">More Coming Soon</h3>
                            <p className="text-sm text-muted-foreground leading-relaxed flex-1">
                              New certifications are being added - stay tuned for updates!
                            </p>
                          </CardContent>
                        </Card>
                      </FadeUpItem>
                    ) : null
                  }
                />
              )}
            </div>
          </ScrollReveal>
        </div>
      </section>

      {/* Features */}
      <section id="features" className="py-12 md:py-24 px-6 bg-muted/70">
        <div className="container mx-auto max-w-6xl">
          <ScrollReveal>
            <h2 className="text-2xl md:text-3xl font-semibold text-foreground text-center mb-3 md:mb-4">
              Everything you need to prepare
            </h2>
            <p className="text-muted-foreground text-center mb-8 md:mb-16 max-w-xl mx-auto">
              A comprehensive toolkit designed for serious certification candidates, starting with ISTQB CTFL and
              expanding to more
            </p>
          </ScrollReveal>

          <ScrollReveal variant="stagger">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              {features.map((f, i) => (
                <FadeUpItem key={i}>
                  <Card className="border border-border shadow-sm h-full transition-[box-shadow,transform,border-color] duration-300 ease-out hover:-translate-y-1.5 hover:shadow-md hover:border-primary/30">
                    <CardContent className="pt-8 pb-6 px-6">
                      <div className="w-12 h-12 rounded-lg bg-primary/10 flex items-center justify-center mb-5">
                        <f.icon className="h-6 w-6 text-primary" />
                      </div>
                      <h3 className="text-lg font-semibold text-foreground mb-2">{f.title}</h3>
                      <p className="text-sm text-muted-foreground leading-relaxed">{f.description}</p>
                    </CardContent>
                  </Card>
                </FadeUpItem>
              ))}
            </div>
          </ScrollReveal>
        </div>
      </section>

      {/* Pricing */}
      <section id="pricing" className="py-12 md:py-24 lg:py-32 px-6 bg-background">
        <div className="container mx-auto max-w-[1120px]">
          <ScrollReveal>
            <h2 className="text-2xl md:text-3xl font-semibold text-foreground text-center mb-3 md:mb-4">
              Choose Your Plan
            </h2>
            <p className="text-muted-foreground text-center mb-10 md:mb-14 max-w-2xl mx-auto">
              All plans include the same features. Choose the subscription period that best fits your certification preparation goals.
            </p>
          </ScrollReveal>

          {/* Free sample callout - a separate, lower-commitment option.
              Deliberately styled as a plain outlined banner, NOT a card,
              so it reads as distinct from the three paid plan cards below. */}
          <ScrollReveal>
            <div className="max-w-3xl mx-auto mb-10 md:mb-14 rounded-lg border border-dashed border-primary/40 bg-primary/[0.03] px-5 py-5 md:px-8 md:py-6 flex flex-col sm:flex-row items-center gap-4 sm:gap-5">
              <div className="flex-1 text-center sm:text-left">
                <p className="text-sm md:text-base font-medium text-foreground">
                  Not sure yet? Try free sample questions, no payment needed.
                </p>
                <p className="text-xs md:text-sm text-muted-foreground mt-1">
                  10 sample exam questions with instant results. No card required.
                </p>
              </div>
              <Button variant="outline" className="shrink-0" asChild>
                <Link to="/signup?intent=trial">
                  Try free sample <ArrowRight className="h-4 w-4" />
                </Link>
              </Button>
            </div>
          </ScrollReveal>

          <ScrollReveal variant="stagger">
            {/* Wider cards (~340px) at lg+ so plan copy doesn't wrap awkwardly.
                Single column under lg for comfortable reading on mobile/tablet. */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 max-w-sm lg:max-w-none mx-auto justify-items-center">
              {/* Monthly */}
              <FadeUpItem>
                <Card className="w-full lg:w-[340px] border border-border shadow-sm h-full flex flex-col transition-[box-shadow,transform,border-color] duration-300 ease-out hover:-translate-y-1 hover:shadow-lg hover:border-primary/30">
                  <CardContent className="pt-7 pb-7 px-7 flex flex-col flex-1">
                    <h3 className="text-lg font-semibold text-foreground text-center mb-1">Monthly</h3>
                    <div className="text-center mb-2">
                      <span className="text-4xl font-bold text-foreground">€{PLAN_PRICES.monthly.amount}</span>
                      <span className="text-muted-foreground text-sm ml-2">/ {PLAN_PRICES.monthly.period}</span>
                    </div>
                    <p className="text-center text-sm font-medium mb-2 invisible" aria-hidden="true">
                      placeholder
                    </p>
                    <p className="text-center text-sm text-muted-foreground mb-5">
                      Ideal for short-term preparation
                    </p>
                    <ul className="space-y-1.5 mb-6">
                      {PLAN_INCLUDED_FEATURES.map((f) => (
                        <li key={f} className="flex items-start gap-2 text-xs text-foreground">
                          <CheckCircle2 className="h-3.5 w-3.5 text-primary mt-0.5 shrink-0" />
                          <span>{f}</span>
                        </li>
                      ))}
                    </ul>
                    <Button variant="outline" className="w-full mt-auto" asChild>
                      <Link to="/signup?plan=monthly">Get Monthly</Link>
                    </Button>
                  </CardContent>
                </Card>
              </FadeUpItem>

              {/* Quarterly */}
              <FadeUpItem>
                <Card className="w-full lg:w-[340px] border-2 border-primary shadow-md relative h-full flex flex-col transition-[box-shadow,transform] duration-300 ease-out hover:-translate-y-1 hover:shadow-xl">
                  <div className="absolute -top-3 left-1/2 -translate-x-1/2">
                    <span className="bg-primary text-primary-foreground text-xs font-semibold px-3 py-1 rounded-full">
                      Most Popular
                    </span>
                  </div>
                  <CardContent className="pt-7 pb-7 px-7 flex flex-col flex-1">
                    <h3 className="text-lg font-semibold text-foreground text-center mb-1">Quarterly</h3>
                    <div className="text-center mb-2">
                      <span className="text-4xl font-bold text-foreground">€{PLAN_PRICES.quarterly.amount}</span>
                      <span className="text-muted-foreground text-sm ml-2">/ {PLAN_PRICES.quarterly.period}</span>
                    </div>
                    <p className="text-center text-sm text-primary font-medium mb-2">
                      Most popular choice · Save {quarterlySavingsPct()}%
                    </p>
                    <p className="text-center text-sm text-muted-foreground mb-5">
                      Ideal for focused exam preparation
                    </p>
                    <ul className="space-y-1.5 mb-6">
                      {PLAN_INCLUDED_FEATURES.map((f) => (
                        <li key={f} className="flex items-start gap-2 text-xs text-foreground">
                          <CheckCircle2 className="h-3.5 w-3.5 text-primary mt-0.5 shrink-0" />
                          <span>{f}</span>
                        </li>
                      ))}
                    </ul>
                    <Button className="w-full mt-auto" asChild>
                      <Link to="/signup?plan=quarterly">Get Quarterly</Link>
                    </Button>
                  </CardContent>
                </Card>
              </FadeUpItem>

              {/* Yearly */}
              <FadeUpItem>
                <Card className="w-full lg:w-[340px] border border-border shadow-sm h-full flex flex-col transition-[box-shadow,transform,border-color] duration-300 ease-out hover:-translate-y-1 hover:shadow-lg hover:border-primary/30">
                  <CardContent className="pt-7 pb-7 px-7 flex flex-col flex-1">
                    <h3 className="text-lg font-semibold text-foreground text-center mb-1">Yearly</h3>
                    <div className="text-center mb-2">
                      <span className="text-4xl font-bold text-foreground">€{PLAN_PRICES.yearly.amount}</span>
                      <span className="text-muted-foreground text-sm ml-2">/ {PLAN_PRICES.yearly.period}</span>
                    </div>
                    <p className="text-center text-sm text-primary font-medium mb-2">
                      Best long-term value · Save {yearlySavingsPct()}%
                    </p>
                    <p className="text-center text-sm text-muted-foreground mb-5">
                      Best for long-term preparation
                    </p>
                    <ul className="space-y-1.5 mb-6">
                      {PLAN_INCLUDED_FEATURES.map((f) => (
                        <li key={f} className="flex items-start gap-2 text-xs text-foreground">
                          <CheckCircle2 className="h-3.5 w-3.5 text-primary mt-0.5 shrink-0" />
                          <span>{f}</span>
                        </li>
                      ))}
                    </ul>
                    <Button variant="outline" className="w-full mt-auto" asChild>
                      <Link to="/signup?plan=yearly">Get Yearly</Link>
                    </Button>
                  </CardContent>
                </Card>
              </FadeUpItem>
            </div>
          </ScrollReveal>
        </div>
      </section>

      {/* FAQ */}
      <section id="faq" className="py-12 md:py-24 lg:py-32 px-6 bg-muted/70">
        <div className="container mx-auto max-w-5xl">
          <ScrollReveal>
            <h2 className="text-2xl md:text-3xl lg:text-4xl font-semibold tracking-tight text-foreground text-center mb-3 md:mb-4">
              Frequently Asked Questions
            </h2>
            <p className="text-muted-foreground text-center text-sm md:text-base mb-8 md:mb-16 lg:mb-20">
              Everything you need to know about the platform, your subscription, and how to study effectively.
            </p>
          </ScrollReveal>
          <ScrollReveal>
            <Accordion
              type="single"
              collapsible
              className="bg-background border border-border rounded-lg shadow-sm divide-y divide-border overflow-hidden"
            >
              {[
                {
                  q: "Which certifications are supported?",
                  a: "We currently focus on ISTQB-aligned certifications, with more being added over time. You can see the full list in the Certifications section above. Each certification has its own dedicated question bank, mock exams, and chapters.",
                },
                {
                  q: "How are the practice questions and mock exams structured?",
                  a: "Practice questions are organised by chapter and learning objective so you can target weak areas. Mock exams replicate the official exam format (same question count, time limit, and passing threshold) so you know exactly where you stand before exam day.",
                },
                {
                  q: "Does this platform provide official certification?",
                  a: "No. We are an independent exam-preparation platform and are not affiliated with ISTQB or any other certification body. Official certification can only be issued by the accredited certification provider after you pass their official exam. Our role is to help you prepare with confidence.",
                },
                {
                  q: "How does the subscription work?",
                  a: "Each subscription is purchased for a single certification. Choose Monthly, Quarterly, or Yearly to unlock that certification's full question bank, mock exams, keyword practice, and detailed insights. Want a second certification later? Buy it separately at any time. Billing renews automatically at the end of each period until you cancel.",
                },
                {
                  q: "Can I cancel anytime?",
                  a: "Yes. You can cancel your subscription at any time from your account settings. You'll keep access until the end of your current billing period, and your progress and history are preserved.",
                },
                ...(IS_BETA
                  ? [
                      {
                        q: "Is the platform in Beta?",
                        a: "Yes. We are in Beta, actively adding certifications, questions, and refinements. Existing functionality is stable, your progress is preserved, and pricing is locked in for the duration of any plan you start during Beta. We'll give clear notice before any change.",
                      },
                      {
                        q: "Why would I subscribe during Beta?",
                        a: "Beta subscribers get full access at the lowest price we plan to offer, priority influence on which certifications and features we ship next, and preferential pricing if rates change at general availability. Your data is safe, secure, and stays yours.",
                      },
                    ]
                  : []),
                {
                  q: "Is my data secure?",
                  a: "Yes. Authentication, role-based access, and per-user data isolation are enforced at the database level. Sensitive question data (correct answers and explanations) is never exposed to the browser.",
                },
              ].map((item, i) => (
                <AccordionItem
                  key={i}
                  value={`faq-${i}`}
                  className="border-0 transition-colors duration-200 hover:bg-secondary/40 data-[state=open]:bg-secondary/30"
                >
                  <AccordionTrigger className="px-6 md:px-8 lg:px-10 text-base lg:text-lg font-semibold text-foreground text-left py-5 lg:py-6 rounded-md outline-none transition-colors hover:no-underline hover:text-primary data-[state=open]:text-primary focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 focus-visible:ring-offset-background [&>svg]:text-muted-foreground hover:[&>svg]:text-primary data-[state=open]:[&>svg]:text-primary [&>svg]:transition-colors">
                    {item.q}
                  </AccordionTrigger>
                  <AccordionContent className="px-6 md:px-8 lg:px-10 text-sm lg:text-base text-muted-foreground pb-5 lg:pb-6 leading-relaxed">
                    {item.a}
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </ScrollReveal>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-6 md:py-12 px-6 border-t border-border bg-sidebar text-sidebar-foreground">
        <div className="container mx-auto max-w-6xl">
          <div className="grid grid-cols-2 md:grid-cols-3 gap-6 md:gap-12 mb-6 md:mb-10 items-start">
            <div className="col-span-2 md:col-span-1 text-center md:text-left mb-2 md:mb-0">
              <img src={logo} alt="A Software Tester" className="h-5 mb-2 brightness-0 invert mx-auto md:mx-0" />
              <p className="text-xs text-sidebar-foreground/60">
                Certification preparation platform built by Testers
              </p>
              <div className="mt-3 flex items-center gap-3 justify-center md:justify-start">
                <a
                  href="https://lt.linkedin.com/company/a-software-tester"
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label="A Software Tester on LinkedIn"
                  className="text-sidebar-foreground/70 hover:text-sidebar-foreground transition-colors"
                >
                  <Linkedin className="h-5 w-5" />
                </a>
                <a
                  href="https://www.instagram.com/a_software_tester"
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label="A Software Tester on Instagram"
                  className="text-sidebar-foreground/70 hover:text-sidebar-foreground transition-colors"
                >
                  <Instagram className="h-5 w-5" />
                </a>
              </div>
            </div>
            <div className="text-center md:text-left">
              <h4 className="font-semibold text-xs uppercase tracking-wide mb-2 md:mb-3">Product</h4>
              <ul className="space-y-1.5 text-sm text-sidebar-foreground/70">
                <li>
                  <button
                    onClick={() => scrollTo("demo")}
                    className="hover:text-sidebar-foreground transition-colors"
                  >
                    How It Works
                  </button>
                </li>
                <li>
                  <button
                    onClick={() => scrollTo("certifications")}
                    className="hover:text-sidebar-foreground transition-colors"
                  >
                    Certifications
                  </button>
                </li>
                <li>
                  <button
                    onClick={() => scrollTo("features")}
                    className="hover:text-sidebar-foreground transition-colors"
                  >
                    Features
                  </button>
                </li>
                <li>
                  <button
                    onClick={() => scrollTo("pricing")}
                    className="hover:text-sidebar-foreground transition-colors"
                  >
                    Pricing
                  </button>
                </li>
                <li>
                  <button onClick={() => scrollTo("faq")} className="hover:text-sidebar-foreground transition-colors">
                    FAQ
                  </button>
                </li>
              </ul>
            </div>
            <div className="text-center md:text-left">
              <h4 className="font-semibold text-xs uppercase tracking-wide mb-2 md:mb-3">Company</h4>
              <ul className="space-y-1.5 text-sm text-sidebar-foreground/70">
                <li>
                  <Link to="/about" className="hover:text-sidebar-foreground transition-colors">
                    About
                  </Link>
                </li>
                <li>
                  <Link to="/contact" className="hover:text-sidebar-foreground transition-colors">
                    Contact
                  </Link>
                </li>
                <li>
                  <Link to="/privacy" className="hover:text-sidebar-foreground transition-colors">
                    Privacy
                  </Link>
                </li>
                <li>
                  <Link to="/terms" className="hover:text-sidebar-foreground transition-colors">
                    Terms
                  </Link>
                </li>
              </ul>
            </div>
          </div>
          <div className="border-t border-sidebar-border pt-4 md:pt-6">
            <p className="text-center text-xs text-sidebar-foreground/40">
              © 2026 | MB "A Software Tester" | Vilnius, Lithuania
            </p>
          </div>
        </div>
      </footer>

      {/* Demo Screenshot Carousel */}
      {/* Certification Detail Dialog */}
      <Dialog open={certDialogOpen} onOpenChange={(open) => !open && setSelectedCert(null)}>
        <DialogContent className="max-w-3xl flex flex-col max-h-[calc(100dvh-2rem)] p-4 sm:p-6">
          {selectedCert && (() => {
            const stats = certStats[selectedCert.certification_id];
            const isActive = selectedCert.status === "active";
            const statValue = (n: number | undefined) =>
              typeof n === "number" ? n.toLocaleString() : "—";
            return (
            <>
              <DialogHeader className="text-left sm:text-left">
                <div className="flex items-start gap-4 mb-2">
                  <div
                    className={`w-12 h-12 rounded-lg flex items-center justify-center shrink-0 ${isActive ? "bg-primary/10" : "bg-muted"}`}
                  >
                    {isActive ? (
                      <Award className="h-6 w-6 text-primary" />
                    ) : (
                      <GraduationCap className="h-6 w-6 text-muted-foreground" />
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap mb-1">
                      <DialogTitle className="text-xl">{selectedCert.name}</DialogTitle>
                      {isActive && Array.isArray(selectedCert.badges) && selectedCert.badges.includes("most_popular") && (
                        <span className="inline-flex items-center gap-1 rounded-full bg-primary text-primary-foreground px-2.5 py-0.5 text-xs font-medium">
                          <Star className="h-3 w-3" /> Most popular certification
                        </span>
                      )}
                      {!isActive && (
                        <span className="inline-flex items-center gap-1 rounded-full bg-muted px-2.5 py-0.5 text-xs font-medium text-muted-foreground">
                          <Clock className="h-3 w-3" /> Coming Soon
                        </span>
                      )}
                    </div>
                    <DialogDescription className="text-sm">
                      {selectedCert.full_name || selectedCert.name}
                    </DialogDescription>
                  </div>
                </div>
              </DialogHeader>

              <div className="flex-1 min-h-0 overflow-y-auto space-y-5 px-1 pr-2">
                {selectedCert.description ? (
                  <div>
                    <div
                      className={`prose prose-sm max-w-none text-muted-foreground leading-relaxed text-justify ${descExpanded ? "" : "line-clamp-4"}`}
                      dangerouslySetInnerHTML={{ __html: sanitizeHtml(selectedCert.description) }}
                    />
                    <button
                      type="button"
                      onClick={() => setDescExpanded((v) => !v)}
                      className="mt-1 text-xs font-medium text-primary hover:underline px-1"
                    >
                      {descExpanded ? "Show less" : "Read more"}
                    </button>
                  </div>
                ) : (
                  <p className="text-sm text-muted-foreground">More details about this certification are coming soon.</p>
                )}

                {/* Certification */}
                <div className="border-t border-border/60 pt-4">
                  <h4 className="text-xs uppercase tracking-wide text-muted-foreground font-medium mb-2">
                    Certification
                  </h4>
                  <dl className="space-y-1 text-sm">
                    <div className="flex justify-between gap-4">
                      <dt className="text-muted-foreground">Syllabus version</dt>
                      <dd className="text-foreground font-medium">v{selectedCert.version}</dd>
                    </div>
                    <div className="flex justify-between gap-4">
                      <dt className="text-muted-foreground">Status</dt>
                      <dd className="text-foreground font-medium">{isActive ? "Active" : "Coming Soon"}</dd>
                    </div>
                    {selectedCert.available_until && (
                      <div className="flex justify-between gap-4">
                        <dt className="text-muted-foreground">Support ends</dt>
                        <dd className="text-foreground font-medium">
                          {format(parseISO(selectedCert.available_until), "d MMMM yyyy")}
                        </dd>
                      </div>
                    )}
                  </dl>
                </div>

                {isActive && (
                  <>
                    {/* Practice Content */}
                    <div className="border-t border-border/60 pt-4">
                      <h4 className="text-xs uppercase tracking-wide text-muted-foreground font-medium mb-3">
                        Practice Content
                      </h4>
                      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
                        {[
                          { label: "Questions", value: stats?.questions_count },
                          { label: "Mock Exam Sets", value: stats?.mock_sets_count },
                          { label: "Keywords", value: stats?.keywords_count },
                          { label: "Chapters", value: stats?.chapters_count },
                        ].map((s) => (
                          <div key={s.label} className="text-center">
                            <div className="text-3xl font-bold text-primary">{statValue(s.value)}</div>
                            <div className="text-xs text-muted-foreground mt-1">{s.label}</div>
                          </div>
                        ))}
                      </div>
                    </div>

                    {/* Practice Experience */}
                    <div className="border-t border-border/60 pt-4">
                      <h4 className="text-xs uppercase tracking-wide text-muted-foreground font-medium mb-3">
                        Practice Experience
                      </h4>
                      <ul className="grid grid-cols-1 sm:grid-cols-2 gap-y-2 gap-x-4 text-sm">
                        {[
                          "Chapter practice",
                          "Keywords practice",
                          "Timed mock exams",
                          "Progress tracking",
                          "Readiness insights",
                          ...((stats?.explanations_count ?? 0) > 0 ? ["Detailed explanations"] : []),
                        ].map((item) => (
                          <li key={item} className="flex items-center gap-2">
                            <CheckCircle2 className="h-4 w-4 text-primary shrink-0" />
                            <span className="text-foreground">{item}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  </>
                )}

                <p className="text-xs text-muted-foreground border-t border-border/60 pt-3">
                  Based on official syllabus and publicly available sample materials. Additional practice content may be added over time.
                </p>
              </div>

              <div className="flex flex-col-reverse sm:flex-row sm:justify-end gap-2 pt-3 border-t border-border mt-2 shrink-0">
                <Button variant="outline" onClick={() => setSelectedCert(null)}>
                  Close
                </Button>
                {isActive && (
                  <Button
                    onClick={() => {
                      setSelectedCert(null);
                      requestAnimationFrame(() => {
                        requestAnimationFrame(() => scrollTo("pricing"));
                      });
                    }}
                    className="gap-2"
                  >
                    Choose Plan <ArrowRight className="h-4 w-4" />
                  </Button>
                )}
              </div>
            </>
            );
          })()}
        </DialogContent>
      </Dialog>

      {/* Floating scroll-to-top button */}
      <ScrollToTopButton />
    </div>
  );
};

export default Landing;

/* ──────────────────────────────────────────────────────────────
 * Product video section
 *
 * Lives directly under the hero so the product is visible without
 * scrolling on common desktop resolutions. Auto-plays once (muted)
 * after the hero brush animation completes (~1.8s). Never loops.
 * A visible play button allows manual replay.
 * ────────────────────────────────────────────────────────────── */
function ProductVideoSection() {
  const videoRef = useRef<HTMLVideoElement>(null);
  const [hasPlayed, setHasPlayed] = useState(false);
  const [ended, setEnded] = useState(false);
  const [blocked, setBlocked] = useState(false);

  useEffect(() => {
    const v = videoRef.current;
    if (!v || hasPlayed) return;
    v.muted = true;
    const tryPlay = () =>
      v
        .play()
        .then(() => {
          setHasPlayed(true);
          setBlocked(false);
        })
        .catch(() => setBlocked(true));
    // Attempt immediately (native autoPlay attr will also try), then again
    // after the hero brush animations finish (~3.5s) in case the first try
    // was throttled while the tab was still warming up.
    tryPlay();
    const t = setTimeout(tryPlay, 3500);
    return () => clearTimeout(t);
  }, [hasPlayed]);

  const replay = () => {
    const v = videoRef.current;
    if (!v) return;
    v.currentTime = 0;
    v.muted = true;
    setEnded(false);
    void v
      .play()
      .then(() => {
        setHasPlayed(true);
        setBlocked(false);
      })
      .catch(() => setBlocked(true));
  };

  return (
    <section id="demo" className="px-6 pb-12 md:pb-16 bg-background scroll-mt-20">
      <div className="container mx-auto max-w-4xl">
        <div className="relative aspect-[1292/910] w-full overflow-hidden rounded-xl border border-border bg-card shadow-sm">
          <img
            src={productVideoPoster.url}
            alt="Certification platform dashboard preview"
            className="absolute inset-0 h-full w-full object-contain"
            loading="lazy"
            decoding="async"
          />
          <video
            ref={videoRef}
            className="absolute inset-0 h-full w-full object-contain"
            autoPlay
            muted
            playsInline
            preload="auto"
            onEnded={() => setEnded(true)}
            poster={productVideoPoster.url}
          >
            <source src={productVideo.url} type="video/mp4" />
          </video>
          {(blocked || ended) && (
            <button
              type="button"
              onClick={replay}
              aria-label={ended ? "Replay product demo video" : "Play product demo video"}
              className="absolute inset-0 flex items-center justify-center bg-black/20 transition hover:bg-black/30 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
            >
              <span className="flex h-16 w-16 items-center justify-center rounded-full bg-white/90 shadow-lg">
                <svg viewBox="0 0 24 24" className="ml-1 h-7 w-7 fill-primary" aria-hidden="true">
                  <path d="M8 5v14l11-7z" />
                </svg>
              </span>
            </button>
          )}
        </div>
      </div>
    </section>
  );
}
